/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { useSwipeable } from 'react-swipeable';
import { theme } from '../../../domain/theme';
import { Actionable } from '../../responsive/atoms/Actionable';
import { IconChevronLeft } from '../../Icon/IconChevronLeft';
import { IconChevronRight } from '../../Icon/IconChevronRight';
import { ImageThumbnailMenu } from './ImageThumbnailMenu';
import { Heading } from './Heading';
import { Description } from './Description';
import { ParagraphWrapper } from '../ParagraphWrapper';
import { getColors } from '../../../domain/utils/colorUtils';
import { t } from '../../../domain/services/configService';
import { usePrevious } from '../../../hooks/usePrevious';

const cardClassByOrder = {
  '-1': 'previous-1',
  0: 'selected',
  1: 'next-1'
};

const getCardClass = cardOrder => {
  return cardClassByOrder[cardOrder] || 'hide';
};

const getOrder = (items, selectedItem) => {
  const middle = Math.round(items.length / 2);
  const isEven = items.length % 2 === 0;
  const initMiddle = isEven ? -middle : -middle + 1;
  let thisOrder = 0;
  let order = items.map((val, index) => {
    if (index > selectedItem) {
      thisOrder += 1;
      if (thisOrder >= middle) {
        thisOrder = initMiddle;
      }
    }
    return { order: thisOrder, index };
  });
  order = order.map((val, index) => {
    if (val.order === 0 && index !== selectedItem) {
      thisOrder += 1;
      if (thisOrder >= middle) {
        thisOrder = initMiddle;
      }
      return { ...val, order: thisOrder };
    }
    return val;
  });
  return order;
};

const ImageCoverFlowWrapperInner = ({
  items,
  allHaveTheSameOrientation,
  thumbnailMenuImages,
  widthSize,
  backgroundColor,
  heading,
  description,
  layout,
  isAboveTheFold
}) => {
  const [selectedItem, setSelectedItem] = useState(0);
  const previousSelectedItem = usePrevious(selectedItem);
  const isMoreThanOneStep =
    Math.abs(selectedItem - previousSelectedItem) > 1 &&
    Math.abs(selectedItem - previousSelectedItem) !== items.length - 1;

  const colors = getColors(backgroundColor);
  const fontColor = colors.color;

  // Keep this line until we find out if calculatedLayout is really required
  // const calculatedLayout = allHaveTheSameOrientation ? layout : 'non-layered';
  const calculatedLayout = layout;

  const styles = {
    container: css`
      background-color: ${backgroundColor};
      color: ${fontColor};
    `,
    header: css`
      display: flex;
      flex-direction: column;
      padding: 56px 20px 0 20px;
      position: relative;
      ${theme.mq.tablet} {
        padding: 80px 50px 16px 50px;
      }
    `,
    yellowLine: css`
      background-color: #fc0;
      height: 3px;
      margin: 0 auto 20px auto;
      width: 40px;
    `,
    carouselWrapper: css`
      display: block;
      padding: 20px 20px 68px 20px;
      ${theme.mq.tablet} {
        justify-items: center;
        margin: 0 auto;
        padding-bottom: 82px;
        max-width: ${widthSize === 'medium' ? '1600px' : 'inherit'};
        width: ${widthSize === 'medium' ? null : 'fit-content'};
      }
      ${theme.mq.small_desktop} {
        padding-bottom: 116px;
      }
    `,
    carouselGallery: css`
      display: grid;
      gap: 20px;
      position: relative;

      ${theme.mq.tablet} {
        display: block;
        margin: 0 auto 24px;
        max-width: ${widthSize === 'medium' ? '1080px' : 'inherit'};
        width: ${widthSize === 'medium' ? '100%' : 'fit-content'};
      }
    `,
    carousel: css`
      align-items: center;
      display: flex;
      height: min-content;
      justify-content: center;
      position: relative;
    `,
    carouselContent: css`
      margin: 20px auto 0 auto;
      max-width: 960px;
    `,
    card: css`
      picture {
        margin: 0 auto;
        img {
          display: block;
          height: 100%;
          margin: auto;
          max-width: 100%;
          min-width: 100%;
          object-fit: contain;
          width: auto;
        }
      }
      display: none;
      transition: all 0.3s;
      max-height: 720px;
      max-width: 100%;
      min-height: 180px;
      min-width: 100%;
      overflow: hidden;
      transition: opacity 0.5s ease;
      ${theme.mq.tablet} {
        min-width: 280px;
        width: 90%;
        transition: ${isMoreThanOneStep
          ? null
          : 'opacity 0.5s ease, transform 0.5s ease, z-index 0.2s step-end'};
      }

      &.selected {
        display: block;
        transform: translateX(0) scale(1);
        z-index: 2;
        opacity: 1;
      }

      &.next-1,
      &.previous-1 {
        display: none;
        ${theme.mq.tablet} {
          display: ${calculatedLayout === 'layered' ? 'flex' : null};
          position: absolute;
          z-index: 1;
          opacity: 0.7;
        }
        &:hover {
          cursor: pointer;
        }
      }
      &.next-1 {
        ${theme.mq.tablet} {
          transform: translateX(16%) scale(0.8);
        }
        ${theme.mq.small_desktop} {
          transform: translateX(120px) scale(0.8);
        }
        ${theme.mq.large_desktop} {
          transform: translateX(25%) scale(0.8);
        }
      }
      &.previous-1 {
        ${theme.mq.tablet} {
          transform: translateX(-16%) scale(0.8);
        }
        ${theme.mq.small_desktop} {
          transform: translateX(-120px) scale(0.8);
        }
        ${theme.mq.large_desktop} {
          transform: translateX(-25%) scale(0.8);
        }
      }
    `,
    cardByOrientation: {
      landscape: css`
        aspect-ratio: ${allHaveTheSameOrientation ? '16 / 9' : '1 / 1'};
        ${theme.mq.small_desktop} {
          aspect-ratio: 16 / 9;
         ${
           (widthSize === 'full-width' || !allHaveTheSameOrientation) &&
           'height: inherit; max-height: inherit; object-fit: contain; max-width: 1400px; width: 68vw;'
         };
        }
      }
      `,
      portrait: css`
        aspect-ratio: ${allHaveTheSameOrientation ? '3 / 4' : '1 / 1'};
        ${theme.mq.small_desktop} {
          ${!allHaveTheSameOrientation &&
          'aspect-ratio: 16 / 9; height: inherit; max-height: inherit; object-fit: contain; max-width: 1400px; width: 68vw;'}
        }
      `,
      square: css`
        aspect-ratio: 1 / 1;
        ${theme.mq.small_desktop} {
          ${!allHaveTheSameOrientation &&
          'aspect-ratio: 16 / 9; height: inherit; max-height: inherit; object-fit: contain; max-width: 1400px; width: 68vw;'}
        }
      `
    },
    icons: css`
      position: absolute;
      top: 0;
      left: 12px;
      width: 40px;
      ${theme.mq.tablet} {
        left: calc(5% + 16px);
      }
      ${theme.mq.small_desktop} {
        left: 24px;
      }
      display: grid;
      z-index: 5;
      height: 100%;
      svg {
        background-color: #0006;
        border-radius: 50%;
        padding: 8px;
        width: 36px;
        height: 36px;
        margin: 0 auto;
      }
    `,
    next: css`
      right: 12px;
      ${theme.mq.tablet} {
        right: calc(5% + 16px);
        left: unset;
      }
      ${theme.mq.small_desktop} {
        right: 24px;
      }
      left: unset;
    `
  };

  const rawOrder = getOrder(items, selectedItem);
  const order = rawOrder.map(val => val.order);
  const getPrevious = () =>
    selectedItem === 0 ? items.length - 1 : selectedItem - 1;
  const getNext = () =>
    selectedItem + 1 === items.length ? 0 : selectedItem + 1;
  const swipeableHandlers = useSwipeable({
    onSwipedLeft: () => {
      setSelectedItem(getNext());
    },
    onSwipedRight: () => {
      setSelectedItem(getPrevious());
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const onCardClick = index => {
    if (getCardClass(order[index]) !== 'selected') {
      setSelectedItem(index);
    }
  };

  return items.length >= 3 ? (
    <section css={styles.container}>
      {(!!heading || !!description) && (
        <header css={styles.header}>
          {!!heading && <Heading text={heading} fontColor={fontColor} />}
          {!!description && (
            <Description text={description} fontColor={fontColor} />
          )}
          <div css={styles.yellowLine} />
        </header>
      )}
      <div css={styles.carouselWrapper}>
        <div css={styles.carouselGallery}>
          {thumbnailMenuImages ? (
            <ImageThumbnailMenu
              images={thumbnailMenuImages}
              selectedImage={selectedItem}
              onClickCb={onCardClick}
              backgroundColor={backgroundColor}
              isAboveTheFold={isAboveTheFold}
            />
          ) : null}
          <div {...swipeableHandlers} css={styles.carousel}>
            <div css={styles.icons}>
              <Actionable
                title={t('Previous')}
                onClick={() => setSelectedItem(getPrevious())}
              >
                <IconChevronLeft size={20} display="block" color="white" />
              </Actionable>
            </div>
            {items.map((item, index) => {
              const cardClass = getCardClass(order[index]);
              return (
                <div
                  className={`${cardClass} ${widthSize}`}
                  css={[
                    styles.card,
                    styles.cardByOrientation[item.orientation]
                  ]}
                  onClick={() => onCardClick(index)}
                  onKeyDown={() => onCardClick(index)}
                  role="button"
                  aria-label={t('View')}
                  tabIndex={0}
                  key={item.key}
                >
                  {item.element}
                </div>
              );
            })}
            <div css={[styles.icons, styles.next]}>
              <Actionable
                title={t('Next')}
                onClick={() => setSelectedItem(getNext())}
              >
                <IconChevronRight size={20} display="block" color="white" />
              </Actionable>
            </div>
          </div>
        </div>
        <div css={styles.carouselContent}>{items[selectedItem]?.caption}</div>
      </div>
    </section>
  ) : null;
};
ImageCoverFlowWrapperInner.defaultProps = {
  thumbnailMenuImages: undefined,
  widthSize: 'full-width',
  backgroundColor: 'white',
  heading: undefined,
  description: undefined,
  layout: 'layered',
  isAboveTheFold: false
};

ImageCoverFlowWrapperInner.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.any,
      orientation: PropTypes.oneOf(['landscape', 'portrait', 'square']),
      element: PropTypes.any,
      caption: PropTypes.any
    })
  ).isRequired,
  allHaveTheSameOrientation: PropTypes.bool.isRequired,
  thumbnailMenuImages: PropTypes.arrayOf(PropTypes.any),
  widthSize: PropTypes.oneOf(['medium', 'full-width']),
  backgroundColor: PropTypes.string,
  heading: PropTypes.string,
  description: PropTypes.string,
  layout: PropTypes.oneOf(['layered', 'non-layered']),
  isAboveTheFold: PropTypes.bool
};

export const ImageCoverFlowWrapper = ParagraphWrapper(
  ImageCoverFlowWrapperInner
);
